import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Product } from 'src/app/services/api.service';
import { ButtonComponent } from '../button/button.component';

/**
 * Individual tariff product display component.
 *
 * @remarks Displays a single tariff product with its details and pricing information.
 * Provides currency formatting utilities and validation for product data.
 * Used within carousels and product listings to show tariff plans consistently.
 */
@Component({
  selector: 'app-tariff',
  templateUrl: './tariff.component.html',
  styleUrls: ['./tariff.component.scss'],
  standalone: true,
  imports: [CommonModule, RouterModule, ButtonComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TariffComponent implements OnInit {
  /** @remarks Product data to be displayed in the tariff component */
  @Input() product: Product;

  /**
   * Component initialization lifecycle hook.
   *
   * @remarks Validates that product data is available and logs a warning
   * if the required product input is not provided. Essential for ensuring
   * the component has the necessary data to render properly.
   */
  ngOnInit(): void {
    // Make sure product data is initialized
    if (!this.product) {
      console.warn('Product data is not available');
    }
  }

  /**
   * Converts a numeric value to formatted Euro currency string.
   *
   * @remarks Formats prices for display in German locale with Euro currency symbol.
   * Handles both string and numeric inputs, preserving existing Euro formatting
   * and converting numeric values to proper currency format.
   *
   * @param value - The price value to format, either as number or string
   * @returns Formatted currency string in German Euro format
   */
  public toEur(value: number | string): string {
    return typeof value === 'string' && value.includes('€')
      ? value
      : Number(value).toLocaleString('de-DE', {
          style: 'currency',
          currency: 'EUR',
        });
  }
}
